import { Box, Button, Modal, Text, TextButton, Toast } from '@zillow/constellation-10';
import { useState } from 'react';
import { useToast } from '@zillow/constellation-10';
import { useMutation } from '@zg-rentals/react-query5';
import {
  CustomerAnalyticsMoodysWebApiHandler_getMoodysReport,
  CustomerAnalyticsMoodysWebApiHandler_getMoodysReport_Path,
} from '@zg-rentals/ts-customer-analytics-client';
import { renderMarketTrendsPdf } from '../../../../utils/pdfRenderer';
import { logError } from '@zg-rentals/log-error';
import { DatarouterError } from '@zg-rentals/rental-manager-env';

type MarketTrendsDownloadCellProps = {
  listingAlias: string;
};

export const MarketTrendsDownloadCell = ({ listingAlias }: MarketTrendsDownloadCellProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const { enqueueToast } = useToast();

  const { mutate: handleDownload, isPending } = useMutation({
    mutationKey: [CustomerAnalyticsMoodysWebApiHandler_getMoodysReport_Path, listingAlias],
    mutationFn: () => CustomerAnalyticsMoodysWebApiHandler_getMoodysReport({ listingAlias }),
    onMutate: () => {
      enqueueToast(<Toast tone="info">Downloading your market trends PDF.</Toast>);
      setIsOpen(false);
    },
    onSuccess: async (report) => {
      try {
        await renderMarketTrendsPdf(report);
      } catch (e) {
        logError({ error: e, context: 'getMoodysReport renderMarketTrendsPdf', errorType: 'MoodysPdfRenderFailure' });
        const errorMessage = DatarouterError.getDisplayMessage(e, 'Failed to generate PDF. Please try again later.');
        enqueueToast(<Toast tone="critical">{errorMessage}</Toast>);
      }
    },
    onError: (e) => {
      const errorMessage = DatarouterError.getDisplayMessage(e, 'Error fetching report, please refresh and try again.');
      enqueueToast(<Toast tone="critical">{errorMessage}</Toast>);
      logError({ error: e, context: 'getMoodysReport', errorType: 'MoodysApiFailure' });
    },
  });

  return (
    <>
      <TextButton css={{ whiteSpace: 'nowrap' }} onClick={() => setIsOpen(true)}>
        Download
      </TextButton>

      <Modal
        open={isOpen}
        onOpenChange={(isModalOpen) => {
          if (!isModalOpen) setIsOpen(false);
        }}
        size="md"
        header="Download Market Trends Report"
        body={
          <Box css={{ padding: '600' }}>
            <Box css={{ display: 'flex', flexDirection: 'column', gap: '200' }}>
              <Text css={{ fontWeight: 'bold', fontSize: 'sm' }}>Report Contents</Text>
              <Text>Market Map</Text>
              <Text>Key Performance Indicators</Text>
              <Text>Asking Rent & Vacancy Trends</Text>
              <Text>Market Inventory</Text>
            </Box>
          </Box>
        }
        footer={
          <Box css={{ display: 'flex', justifyContent: 'flex-end', gap: '400' }}>
            <Button tone="neutral" emphasis="outlined" onClick={() => setIsOpen(false)}>
              Cancel
            </Button>
            <Button tone="brand" emphasis="filled" onClick={() => handleDownload()} disabled={isPending}>
              Download
            </Button>
          </Box>
        }
      />
    </>
  );
};
